import { Button, Dialog, TextInput, Typography, Wizard, toast } from '@neo4j-ndl/react';
import { fleetManagementApi, useActiveProjectSummary } from '@nx/state';
import { isNonEmptyString, isNotNullish, isNullish } from '@nx/stdlib';
import { ErrorBanner } from '@nx/ui/src/errors/error-banner/error-banner';
import { useEffect, useState } from 'react';

import { ApiErrorBanner, InformationIconWithTooltip } from '../../../shared/components';
import { SingleLineCodeBlock } from '../../../shared/components/single-line-code-block';
import { PluginDownloadLinks } from '../../shared/components/plugin-download-links';
import { TokenOutput } from '../../shared/components/token-output';
import { expiryTimeOptions } from '../../shared/constants';
import { TokenAutoRotationOption, TokenExpirySelection } from '../regenerate-token-modal';
import { CreateDeploymentStepComponent } from './create-deployment-step';

export interface StepComponentSharedState {
  token: string | undefined;
  tokenExpiry: { value: Date; label: string } | undefined;
  autoRotate: boolean;
  deploymentId: string | undefined;
}

export interface StepComponentProps {
  setActiveStepIndex: (stepIndex: number) => void;
  stop: () => void;
  sharedState: StepComponentSharedState;
  updateSharedState: (ss: StepComponentSharedState) => void;
}

const fleetManagementStatusExpectedOutput = `│ active │ connected │ statusMessage                      |
│ false  │ false     │ "Fleet management is disabled. ... |
`;

const InstallPluginStepComponent = ({ setActiveStepIndex, stop }: StepComponentProps) => {
  return (
    <>
      <Dialog.Content className="flex flex-col">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col">
            <Typography variant="subheading-small">Step 1: Download the plugin matching your Neo4j version.</Typography>
            <Typography variant="body-medium">
              <PluginDownloadLinks />
            </Typography>
          </div>
          <div className="flex flex-col">
            <Typography variant="subheading-small">Step 2: Install plugin.</Typography>
            <ol className="mx-4 list-disc">
              <li>
                <Typography variant="body-medium">
                  Add the plugin to <code className="bg-palette-neutral-bg-default px-1">plugins</code> directory of
                  your Neo4j installation.
                </Typography>
              </li>
              <li>
                {' '}
                <Typography variant="body-medium">
                  To allow plugin procedures, check and, if needed, update the following properties in the{' '}
                  <code className="bg-palette-neutral-bg-default px-1">neo4j.conf</code> file:
                </Typography>
                <div className="mt-2 flex flex-col gap-2">
                  <details className="p-1">
                    <summary className="cursor-pointer text-sm">
                      <span className="inline-flex items-center gap-1">
                        dbms.security.procedures.unrestricted
                        <InformationIconWithTooltip
                          tooltip="This property allows Fleet Management code to access low-level components of Neo4j."
                          isPortaled={false}
                        />
                      </span>
                    </summary>
                    <Typography variant="body-small">
                      If this property is already set, append{' '}
                      <code className="bg-palette-neutral-bg-default px-1 text-xs">fleetManagement.*</code> to the
                      existing value:
                      <SingleLineCodeBlock
                        code="dbms.security.procedures.unrestricted=<existing-value>,fleetManagement.*"
                        copyButton={false}
                      />
                      If this property is not set, set it:
                      <SingleLineCodeBlock code="dbms.security.procedures.unrestricted=fleetManagement.*" />
                    </Typography>
                  </details>
                  <details className="p-1">
                    <summary className="cursor-pointer text-sm">
                      <span className="inline-flex items-center gap-1">
                        dbms.security.procedures.allowlist
                        <InformationIconWithTooltip
                          tooltip="This property specifies which procedures are allowed to be called."
                          isPortaled={false}
                        />
                      </span>
                    </summary>
                    <Typography variant="body-small">
                      If this property is already set, append{' '}
                      <code className="bg-palette-neutral-bg-default px-1 text-xs">fleetManagement.*</code> to the
                      existing value:
                      <SingleLineCodeBlock
                        code="dbms.security.procedures.allowlist=<existing-value>,fleetManagement.*"
                        copyButton={false}
                      />
                      Otherwise, no additional action is needed.
                    </Typography>
                  </details>
                </div>
              </li>
            </ol>
          </div>
          <div className="flex flex-col">
            <Typography variant="subheading-small">Step 3: Restart the server.</Typography>
          </div>
          <div className="flex flex-col">
            <Typography variant="subheading-small">Step 4: Verify the plugin is installed correctly.</Typography>
            <SingleLineCodeBlock code={'CALL fleetManagement.status()'} language="cypher" />
            <details className="p-1">
              <summary className="cursor-pointer text-sm">
                <span className="inline-flex items-center gap-1">Expected output</span>
              </summary>
              <SingleLineCodeBlock code={fleetManagementStatusExpectedOutput} copyButton={false} />
            </details>
          </div>
        </div>
      </Dialog.Content>
      <Dialog.Actions className="flex flex-row">
        <div className="flex gap-3">
          <Button fill="outlined" color="neutral" onClick={stop}>
            Cancel
          </Button>
          <Button onClick={() => setActiveStepIndex(1)}>Next</Button>
        </div>
      </Dialog.Actions>
    </>
  );
};

const RequestTokenStepComponent = ({
  setActiveStepIndex,
  stop,
  sharedState,
  updateSharedState,
}: StepComponentProps) => {
  const activeProjectSummary = useActiveProjectSummary();
  const [createToken, { error, isLoading }] = fleetManagementApi.useCreateDeploymentTokenMutation();

  return (
    <>
      <Dialog.Content className="flex flex-col gap-4">
        {error && <ApiErrorBanner error={error} />}
        {!error && (
          <>
            {isNotNullish(sharedState.token) && isNotNullish(sharedState.tokenExpiry) ? (
              <TextInput isReadOnly={true} value={sharedState.tokenExpiry.label} label="Token expiry" />
            ) : (
              <div className="flex flex-col gap-2">
                <TokenExpirySelection
                  expiry={sharedState.tokenExpiry}
                  setSelectedExpiry={(option) =>
                    updateSharedState({
                      ...sharedState,
                      tokenExpiry: { label: option.label, value: option.value },
                    })
                  }
                />
                <TokenAutoRotationOption
                  autoRotate={sharedState.autoRotate}
                  setAutoRotate={(autoRotate) => updateSharedState({ ...sharedState, autoRotate })}
                />
              </div>
            )}
          </>
        )}
      </Dialog.Content>
      <Dialog.Actions className="flex justify-between">
        <Button onClick={() => setActiveStepIndex(0)} fill="outlined" color="neutral">
          Back
        </Button>
        <div className="flex gap-3">
          <Button fill="outlined" color="neutral" onClick={stop}>
            Cancel
          </Button>
          <Button
            isDisabled={isNullish(sharedState.tokenExpiry) || isNotNullish(error)}
            onClick={() => {
              void createToken({
                project_id: activeProjectSummary?.id ?? '',
                rotation_interval: sharedState.tokenExpiry?.label ?? '',
                deployment_id: sharedState.deploymentId ?? '',
                auto_rotate: sharedState.autoRotate,
              })
                .then((res) => {
                  if (isNotNullish(res.data) && isNonEmptyString(res.data.token)) {
                    toast.success('Token created', { shouldAutoClose: true });
                    updateSharedState({ ...sharedState, token: res.data.token });
                    setActiveStepIndex(2);
                  }
                })
                .catch((err: Error) => {
                  toast.danger(err.message);
                });
            }}
            isLoading={isLoading}
          >
            Next
          </Button>
        </div>
      </Dialog.Actions>
    </>
  );
};

const RegisterTokenStepComponent = ({ stop, sharedState }: StepComponentProps) => {
  const [hasAcknowledged, acknowledge] = useState<boolean>(false);

  return (
    <>
      <Dialog.Content className="flex flex-col">
        {isNotNullish(sharedState.token) && (
          <TokenOutput token={sharedState.token} hasAcknowledged={hasAcknowledged} acknowledge={acknowledge} />
        )}
      </Dialog.Content>
      <Dialog.Actions className="flex flex-row">
        <div className="flex gap-3">
          <Button onClick={stop} isDisabled={!hasAcknowledged}>
            Done
          </Button>
        </div>
      </Dialog.Actions>
    </>
  );
};

const defaultSharedState = (deploymentId?: string) => ({
  token: undefined,
  tokenExpiry: expiryTimeOptions[0],
  autoRotate: true,
  deploymentId,
});

export const ConnectDeploymentWizardModal = ({
  isOpen,
  closeModal,
  deploymentId,
}: {
  isOpen: boolean;
  closeModal: () => void;
  deploymentId?: string;
}) => {
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const steps = [
    {
      content: 'Install plugin',
      component: InstallPluginStepComponent,
    },
    {
      content: 'Request token',
      component: RequestTokenStepComponent,
    },
    {
      content: 'Register token',
      component: RegisterTokenStepComponent,
    },
  ];

  const [sharedState, updateSharedState] = useState<StepComponentSharedState>(defaultSharedState(deploymentId));

  useEffect(() => {
    updateSharedState((prev) => ({ ...prev, deploymentId }));
  }, [deploymentId, updateSharedState]);

  const stop = () => {
    setActiveStepIndex(0);
    updateSharedState(defaultSharedState(deploymentId));
    closeModal();
  };

  const step = steps[activeStepIndex];
  if (!step) {
    return <ErrorBanner title={'Invalid step'} />;
  }

  const StepComponent = step.component;
  return (
    <Dialog size="large" isOpen={isOpen} onClose={closeModal} hasDisabledCloseButton>
      {isNullish(sharedState.deploymentId) && (
        <>
          <Dialog.Header>
            <Typography variant="h3">Add deployment</Typography>
          </Dialog.Header>
          <CreateDeploymentStepComponent
            setActiveStepIndex={setActiveStepIndex}
            stop={stop}
            sharedState={sharedState}
            updateSharedState={updateSharedState}
          />
        </>
      )}

      {isNotNullish(sharedState.deploymentId) && (
        <>
          <Dialog.Header>
            <Wizard orientation="horizontal" size="large" activeStepIndex={activeStepIndex} steps={steps} />
          </Dialog.Header>
          <StepComponent
            setActiveStepIndex={setActiveStepIndex}
            stop={stop}
            sharedState={sharedState}
            updateSharedState={updateSharedState}
          />
        </>
      )}
    </Dialog>
  );
};
